import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO } from '../../utils/seo';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    furigana: '',
    email: '',
    phone: '',
    category: '',
    address: '',
    message: '',
    agree: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // SEO設定
  useSEO({
    title: 'お問い合わせ｜おかやま解体工事株式会社 - 岡山市の解体・造園工事',
    description: '岡山市のおかやま解体工事株式会社へのお問い合わせページ。電話、メールフォームでのご相談を24時間受付中。解体工事、造園工事、不用品処理など、お気軽にご相談ください。無料見積もり実施中。',
    keywords: 'お問い合わせ,相談,見積もり,岡山市,解体工事,造園工事,電話,メール',
    canonicalPath: '/contact',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'お問い合わせ',
      description: 'おかやま解体工事株式会社へのお問い合わせページ'
    }
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.agree) {
      alert('プライバシーポリシーに同意してください');
      return;
    }

    if (formData.message.length > 500) {
      alert('お問い合わせ内容は500文字以内で入力してください');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('furigana', formData.furigana);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('category', formData.category);
      formBody.append('address', formData.address);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5mpvublpot0rgnb7rk0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          furigana: '',
          email: '',
          phone: '',
          category: '',
          address: '',
          message: '',
          agree: false,
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactMethods = [
    {
      icon: 'ri-phone-line',
      title: 'お電話',
      description: '営業時間内にお気軽にお電話ください',
      link: 'tel:086-012-3456',
      linkText: '086-012-3456',
    },
    {
      icon: 'ri-mail-line',
      title: 'メールフォーム',
      description: '24時間受付中。2営業日以内に返信いたします',
      link: '#form',
      linkText: 'フォームへ',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[300px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/ea8031d63b714950bf124a2f478e496c.jpeg"
            alt="お問い合わせ"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        <div className="relative z-10 container mx-auto px-4 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-4 bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full mb-6">
            <i className="ri-customer-service-line text-white w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-white">24時間受付中</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
            お問い合わせ
          </h1>
          <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto">
            お気軽にご相談ください
          </p>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-primary text-center mb-12">
            お問い合わせ方法をお選びください
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {contactMethods.map((method, index) => (
              <div
                key={index}
                className="bg-white border-2 border-gray-200 rounded-2xl p-10 hover:shadow-xl transition-all text-center"
              >
                <div className="w-20 h-20 bg-secondary rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${method.icon} text-4xl text-white`}></i>
                </div>
                <h3 className="text-primary font-bold text-2xl mb-3">{method.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{method.description}</p>
                <a
                  href={method.link}
                  className="block w-full px-6 py-3 bg-primary text-white rounded-full hover:bg-secondary transition-all font-medium whitespace-nowrap cursor-pointer"
                >
                  {method.linkText}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="form" className="py-16 lg:py-24 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-primary text-center mb-12">
            メールでのお問い合わせ
          </h2>
          <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-md p-8 lg:p-12">
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-8">
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-primary font-medium mb-2">
                  お名前
                  <span className="ml-2 bg-red-500 text-white text-xs px-2 py-1 rounded">必須</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-secondary transition-colors"
                  placeholder="山田太郎"
                />
              </div>

              {/* Furigana */}
              <div>
                <label htmlFor="furigana" className="block text-primary font-medium mb-2">
                  フリガナ
                  <span className="ml-2 bg-red-500 text-white text-xs px-2 py-1 rounded">必須</span>
                </label>
                <input
                  type="text"
                  id="furigana"
                  name="furigana"
                  value={formData.furigana}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-secondary transition-colors"
                  placeholder="ヤマダタロウ"
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-primary font-medium mb-2">
                  メールアドレス
                  <span className="ml-2 bg-red-500 text-white text-xs px-2 py-1 rounded">必須</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-secondary transition-colors"
                  placeholder="example@email.com"
                />
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="phone" className="block text-primary font-medium mb-2">
                  電話番号
                  <span className="ml-2 bg-red-500 text-white text-xs px-2 py-1 rounded">必須</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-secondary transition-colors"
                  placeholder="090-1234-5678"
                />
              </div>

              {/* Category */}
              <div>
                <label htmlFor="category" className="block text-primary font-medium mb-2">
                  お問い合わせ種別
                  <span className="ml-2 bg-red-500 text-white text-xs px-2 py-1 rounded">必須</span>
                </label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-secondary transition-colors cursor-pointer"
                >
                  <option value="">選択してください</option>
                  <option value="建物解体について">建物解体について</option>
                  <option value="内装解体について">内装解体について</option>
                  <option value="造園工事について">造園工事について</option>
                  <option value="不用品処理について">不用品処理について</option>
                  <option value="見積もり依頼">見積もり依頼</option>
                  <option value="その他">その他</option>
                </select>
              </div>

              {/* Address */}
              <div>
                <label htmlFor="address" className="block text-primary font-medium mb-2">
                  ご住所
                  <span className="ml-2 bg-gray-400 text-white text-xs px-2 py-1 rounded">任意</span>
                </label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-secondary transition-colors"
                  placeholder="岡山市北区○○町1-2-3"
                />
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-primary font-medium mb-2">
                  お問い合わせ内容
                  <span className="ml-2 bg-red-500 text-white text-xs px-2 py-1 rounded">必須</span>
                  <span className="ml-2 text-sm text-gray-500">（500文字以内）</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={8}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-secondary transition-colors resize-none"
                  placeholder="お問い合わせ内容をご記入ください"
                ></textarea>
                <div className="text-right text-sm text-gray-500 mt-1">
                  {formData.message.length} / 500文字
                </div>
              </div>

              {/* Privacy Policy */}
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="agree"
                  name="agree"
                  checked={formData.agree}
                  onChange={handleChange}
                  required
                  className="mt-1 w-5 h-5 cursor-pointer"
                />
                <label htmlFor="agree" className="text-gray-700 cursor-pointer">
                  <a href="#" className="text-primary underline hover:text-secondary">
                    プライバシーポリシー
                  </a>
                  に同意する
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-primary text-white rounded-full hover:bg-secondary transition-all font-bold text-lg whitespace-nowrap cursor-pointer disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isSubmitting ? '送信中...' : '送信する'}
              </button>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="p-4 bg-green-50 border-2 border-green-500 rounded-lg text-green-700 text-center">
                  お問い合わせを受け付けました。ご連絡ありがとうございます。
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border-2 border-red-500 rounded-lg text-red-700 text-center">
                  送信に失敗しました。お手数ですが、もう一度お試しください。
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
